import { omit, uniq } from 'lodash';
import { DateTime } from 'luxon';

import { RegistrationSituationStatus } from '@appTypes/enums';
import type {
  AddAdultToSubUnitPayload,
  AddYouthToSubUnitPayload,
  OrgSubUnit,
  PostAddDenChiefRequest,
  PostAddSubUnitRequest,
  PostRemoveDenChiefRequest,
  PutSubUnitRequest,
  RosterParent,
  SubUnitType,
} from '@appTypes/esb';
import {
  subUnitAdultPayload,
  subUnitParentUserIds,
  subUnitYouthPositionId,
} from '@modules/advancement/utilsTyped';
import type {
  CreateSubUnitWithMembersArg,
  DeleteSubUnitWithMembersArg,
  SubUnitSaveStep,
  UpdateSubUnitWithMembersArg,
} from '@modules/roster/subUnitMutations';
import { PositionId, denTypesNames } from '@shared/constants';
import { getPreferredName } from '@shared/utils/preferredName';
import { formatToAPIDate } from '@utils/datesLuxon';

import {
  type DedupedPosition,
  MemberKind,
  type SubUnitAssignment,
} from './rosterMember';
import {
  type RosterMemberView,
  type SubUnitRef,
  assignmentRef,
} from './rosterMemberView';

// --- Permissions -------------------------------------------------------------

export interface SubUnitRights {
  /** `EDIT_UNIT`: any den/patrol, and adding one */
  canEditAll: boolean;
  /** `EDIT_SUB_UNIT`: only the dens/patrols the user is assigned to */
  canEditOwn: boolean;
}

export interface SubUnitPermissions {
  canAdd: boolean;
  /** Some den/patrol may be edited, so the Edit/Delete actions show */
  canManage: boolean;
  /** This den/patrol may be edited or deleted */
  canEdit: (ref: Pick<SubUnitRef, 'type' | 'id'>) => boolean;
}

const NO_PERMISSIONS: SubUnitPermissions = {
  canAdd: false,
  canManage: false,
  canEdit: () => false,
};

export function needsOwnSubUnits(rights: SubUnitRights): boolean {
  return rights.canEditOwn && !rights.canEditAll;
}

/**
 * Nothing without a `subUnitType`. With `EDIT_SUB_UNIT` only, no sub-unit can
 * be edited while the user's assignments load.
 */
export function buildSubUnitPermissions(input: {
  subUnitType?: SubUnitType;
  rights: SubUnitRights;
  myAssignments: readonly SubUnitAssignment[];
}): SubUnitPermissions {
  const { subUnitType, rights, myAssignments } = input;
  const { canEditAll, canEditOwn } = rights;
  if (!subUnitType || (!canEditAll && !canEditOwn)) return NO_PERMISSIONS;

  const mySubUnitIds = new Set<number>();
  myAssignments.forEach(assignment => {
    const ref = assignmentRef(assignment);
    if (ref?.type === subUnitType) mySubUnitIds.add(ref.id);
  });

  return {
    canAdd: canEditAll,
    canManage: true,
    canEdit: ref =>
      ref.type === subUnitType && (canEditAll || mySubUnitIds.has(ref.id)),
  };
}

// --- Den chiefs --------------------------------------------------------------

// Den chiefs come from the Scoutbook unit roster, not the org roster: only it
// has the position records (`positions[].id`) that the den chief calls need

export interface UnitRosterPosition {
  /** `userPositionId` of `denChief/remove` */
  id: number;
  positionId: number;
  /** Set on den positions only */
  denId?: number | null;
}

/** A `RosterYouthMember` or `RosterAdultMember` of the Scoutbook unit roster */
export interface UnitRosterPerson {
  userId: number;
  memberId: number;
  personGuid: string;
  firstName: string;
  lastName: string;
  nickName?: string | null;
  pictureUrl?: string | null;
  gender?: string;
  positions?: readonly UnitRosterPosition[] | null;
}

export interface DenChiefRecord {
  /** `sbUserId` */
  userId: number;
  memberId: number;
  personGuid: string;
  /** Preferred full name */
  name: string;
  pictureUrl?: string | null;
  gender?: string;
  /** First Den Chief position record: the `id` of `denChief/add` */
  positionRecordId?: number;
  /** Per den id: the `userPositionId` of `denChief/remove` */
  positionRecordIdByDenId: Readonly<Record<number, number>>;
}

export type DenChiefsByUserId = Readonly<Record<number, DenChiefRecord>>;

/** Den chiefs by `userId`. A person listed twice has their positions merged. */
export function buildDenChiefRecords(
  people: readonly UnitRosterPerson[] | undefined,
): Record<number, DenChiefRecord> {
  const records: Record<number, DenChiefRecord> = {};
  (people ?? []).forEach(person => {
    const positions = (person.positions ?? []).filter(
      ({ positionId }) => positionId === PositionId.DenChief,
    );
    if (!person.userId || positions.length === 0) return;

    const existing = records[person.userId];
    const positionRecordIdByDenId: Record<number, number> = {
      ...existing?.positionRecordIdByDenId,
    };
    positions.forEach(({ id, denId }) => {
      if (denId != null && positionRecordIdByDenId[denId] === undefined) {
        positionRecordIdByDenId[denId] = id;
      }
    });

    records[person.userId] = {
      userId: person.userId,
      memberId: person.memberId,
      personGuid: person.personGuid,
      name: getPreferredName(person),
      pictureUrl: person.pictureUrl,
      gender: person.gender,
      positionRecordId: existing?.positionRecordId ?? positions[0].id,
      positionRecordIdByDenId,
    };
  });
  return records;
}

export function denChiefIdsInDen(
  denChiefs: DenChiefsByUserId,
  denId: number,
): number[] {
  return Object.values(denChiefs)
    .filter(record => record.positionRecordIdByDenId[denId] !== undefined)
    .map(record => record.userId);
}

export function denIdsOfDenChief(record: DenChiefRecord): number[] {
  return Object.keys(record.positionRecordIdByDenId).map(Number);
}

// --- Member options ----------------------------------------------------------

export type SubUnitMemberKind = 'youth' | 'participant' | 'adult' | 'denChief';

/** `expired`: the sub-unit endpoints reject them (lapsed members are fine) */
export type SubUnitUnavailableReason = 'expired' | 'executiveOfficer';

export interface SubUnitMemberOption {
  /** `sbUserId` */
  value: number;
  /** Preferred full name */
  label: string;
  kind: SubUnitMemberKind;
  /** Adults only: main position; never a functional one */
  detail?: string;
  /** Can't be added, but still listed and removable when already in */
  unavailable?: SubUnitUnavailableReason;
  pictureUrl?: string | null;
  gender?: string;
  /** Labels, excluding the sub-unit being edited */
  currentSubUnits: string[];
}

// --- Who can be added --------------------------------------------------------

export type SubUnitEligibilityMember = Pick<
  RosterMemberView,
  'kind' | 'registrationStatus' | 'positions'
>;

/** Executive Officer is their only unit (non-functional) position */
const isOnlyExecutiveOfficer = (positions: readonly DedupedPosition[]) => {
  const unitPositions = positions.filter(p => !p.isFunctional);
  return (
    unitPositions.length > 0 &&
    unitPositions.every(p => p.positionId === PositionId.ExecutiveOfficer)
  );
};

/** `undefined` when the member can be added. `expired` is checked first. */
export function unavailableReason(
  member: SubUnitEligibilityMember,
): SubUnitUnavailableReason | undefined {
  if (member.registrationStatus === RegistrationSituationStatus.Expired) {
    return 'expired';
  }
  if (
    member.kind === MemberKind.Adult &&
    isOnlyExecutiveOfficer(member.positions)
  ) {
    return 'executiveOfficer';
  }
  return undefined;
}

// --- Picker options ----------------------------------------------------------

const byLabel = (a: SubUnitMemberOption, b: SubUnitMemberOption) =>
  a.label.localeCompare(b.label);

const isEditing = (
  ref: SubUnitRef,
  subUnitType: SubUnitType,
  editingSubUnitId?: number,
) => ref.type === subUnitType && ref.id === editingSubUnitId;

/** Den chiefs are left out of the youths: they have their own tab. */
export function buildMemberOptions(input: {
  members: readonly RosterMemberView[];
  subUnitType: SubUnitType;
  editingSubUnitId?: number;
  denChiefs?: DenChiefsByUserId;
}): {
  youthOptions: SubUnitMemberOption[];
  adultOptions: SubUnitMemberOption[];
} {
  const { members, subUnitType, editingSubUnitId, denChiefs = {} } = input;
  const youthOptions: SubUnitMemberOption[] = [];
  const adultOptions: SubUnitMemberOption[] = [];
  const seen = new Set<number>();

  members.forEach(member => {
    const { sbUserId } = member;
    if (!sbUserId || seen.has(sbUserId)) return;
    seen.add(sbUserId);

    const option = {
      value: sbUserId,
      label: member.preferredName,
      pictureUrl: member.pictureUrl,
      gender: member.gender,
      currentSubUnits: member.subUnits
        .filter(ref => !isEditing(ref, subUnitType, editingSubUnitId))
        .map(ref => ref.label),
      unavailable: unavailableReason(member),
    };

    if (member.kind === MemberKind.Adult) {
      adultOptions.push({
        ...option,
        kind: 'adult',
        detail: member.positions.find(p => !p.isFunctional)?.position,
      });
    } else if (!denChiefs[sbUserId]) {
      youthOptions.push({
        ...option,
        kind: member.kind === MemberKind.Participant ? 'participant' : 'youth',
      });
    }
  });

  return {
    youthOptions: youthOptions.sort(byLabel),
    adultOptions: adultOptions.sort(byLabel),
  };
}

/** `currentSubUnits` are the other dens they serve */
export function buildDenChiefOptions(input: {
  denChiefs: DenChiefsByUserId;
  editingSubUnitId?: number;
  labelOf: (subUnitId: number) => string | undefined;
}): SubUnitMemberOption[] {
  const { denChiefs, editingSubUnitId, labelOf } = input;
  return Object.values(denChiefs)
    .map(
      (record): SubUnitMemberOption => ({
        value: record.userId,
        label: record.name,
        kind: 'denChief',
        pictureUrl: record.pictureUrl,
        gender: record.gender,
        currentSubUnits: denIdsOfDenChief(record)
          .filter(denId => denId !== editingSubUnitId)
          .map(labelOf)
          .filter((label): label is string => !!label)
          .sort((a, b) => a.localeCompare(b)),
      }),
    )
    .sort(byLabel);
}

// --- Inputs ------------------------------------------------------------------

export type SubUnitFormMode = 'add' | 'edit';

/** The form values, also the server baseline in Edit. Ids are `sbUserId`s. */
export interface SubUnitValues {
  /** Raw name, not the display label */
  name: string;
  /** Dens only: a `denTypesNames` value */
  denType?: string;
  /** Youths and participants */
  youthIds: number[];
  adultIds: number[];
  /** Dens only */
  denChiefIds: number[];
}

export interface SubUnitPayloadContext {
  /** Not `useUnitRoster().parents`, which drops a parent's other children */
  parents: readonly RosterParent[];
  /** Dens only: from `buildDenChiefRecords` */
  denChiefs?: DenChiefsByUserId;
  /** Dens only: the pack's `unitId`, the `packId` of `denChief/add` */
  packId?: number;
  /** Expired members: the sub-unit endpoints reject them */
  expiredUserIds?: readonly number[];
}

const today = () => formatToAPIDate(DateTime.now());

const isDenType = (subUnitType: SubUnitType) => subUnitType === 'den';

// --- Position ids and payloads ----------------------------------------------

/** By den level for dens (Youth Member when unknown), Scouts BSA for patrols */
export function youthPositionId(
  subUnitType: SubUnitType,
  denType?: string | null,
): number {
  return subUnitYouthPositionId(isDenType(subUnitType), denType);
}

export function buildYouthPayload(
  userId: number,
  positionId: number,
  parentUserIds: readonly number[] = [],
): AddYouthToSubUnitPayload {
  return {
    userId,
    positionId,
    ...(parentUserIds.length ? { parentUserIds: [...parentUserIds] } : {}),
  };
}

/** Den Admin or Patrol Admin, with full control */
export function buildAdultPayload(
  userId: number,
  subUnitType: SubUnitType,
): AddAdultToSubUnitPayload {
  return subUnitAdultPayload(userId, isDenType(subUnitType));
}

/** No `id` without a position record. The mutations set `denId`. */
export function buildDenChiefAddPayload(
  denChief: DenChiefRecord,
  packId?: number,
): PostAddDenChiefRequest {
  return {
    userId: denChief.userId,
    memberId: denChief.memberId,
    dateStarted: today(),
    ...(denChief.positionRecordId != null
      ? { id: denChief.positionRecordId }
      : {}),
    ...(packId != null ? { packId } : {}),
  };
}

/** `undefined` without a record for the den. The mutations set `denId`. */
export function buildDenChiefRemovePayload(
  denChief: DenChiefRecord,
  subUnitId: number,
): PostRemoveDenChiefRequest | undefined {
  const userPositionId = denChief.positionRecordIdByDenId[subUnitId];
  if (userPositionId === undefined) return undefined;
  return {
    userId: denChief.userId,
    dateEnded: today(),
    userPositionId,
  };
}

// --- Name rules --------------------------------------------------------------

/** Allowed name characters. `-` stays last, so it isn't a range. */
export const SUB_UNIT_NAME_PATTERN = /^[ :_;@%&/A-Za-z0-9.'()-]*$/;
const STARTS_WITH_DIGIT = /^\d/;

export type SubUnitNameError =
  | 'required'
  | 'invalidCharacters'
  | 'mustStartWithDigit'
  | 'taken';

export const normalizeSubUnitName = (name: string) => name.trim().toLowerCase();

export function checkNameRequired(
  name: string | undefined,
): SubUnitNameError | undefined {
  return name?.trim() ? undefined : 'required';
}

export function checkNameCharacters(
  name: string | undefined,
): SubUnitNameError | undefined {
  return SUB_UNIT_NAME_PATTERN.test(name ?? '')
    ? undefined
    : 'invalidCharacters';
}

/** Den names (except AOL dens) must start with a digit. Blank names pass. */
export function checkNameLeadingDigit(
  name: string | undefined,
  subUnitType: SubUnitType,
  denType?: string,
): SubUnitNameError | undefined {
  const trimmed = name?.trim();
  if (!trimmed || !isDenType(subUnitType) || denType === denTypesNames.aol) {
    return undefined;
  }
  return STARTS_WITH_DIGIT.test(trimmed) ? undefined : 'mustStartWithDigit';
}

/** Case-insensitive; the den level is ignored. Blank names pass. */
export function checkNameUnique(
  name: string | undefined,
  takenNames: readonly string[],
): SubUnitNameError | undefined {
  const normalized = normalizeSubUnitName(name ?? '');
  if (!normalized) return undefined;
  return takenNames.some(taken => normalizeSubUnitName(taken) === normalized)
    ? 'taken'
    : undefined;
}

/**
 * The first rule error. An unchanged `storedName` passes as is, so a name
 * saved before these rules doesn't block saving member changes.
 */
export function validateSubUnitName(input: {
  name: string | undefined;
  subUnitType: SubUnitType;
  denType?: string;
  takenNames: readonly string[];
  storedName?: string;
}): SubUnitNameError | undefined {
  const { name, subUnitType, denType, takenNames, storedName } = input;
  const stored = storedName?.trim();
  if (stored && name?.trim() === stored) return undefined;
  return (
    checkNameRequired(name) ??
    checkNameCharacters(name) ??
    checkNameLeadingDigit(name, subUnitType, denType) ??
    checkNameUnique(name, takenNames)
  );
}

/** Every sub-unit, approved or not, except the one being edited */
export function takenSubUnitNames(
  subUnits: readonly Pick<OrgSubUnit, 'subUnitId' | 'subUnitName'>[],
  editingSubUnitId?: number,
): string[] {
  return subUnits
    .filter(({ subUnitId }) => subUnitId !== editingSubUnitId)
    .map(({ subUnitName }) => subUnitName);
}

// --- Baseline ----------------------------------------------------------------

export interface SubUnitBaselineMember {
  sbUserId: number;
  kind: MemberKind;
  subUnits: readonly SubUnitRef[];
}

/**
 * The sub-unit as the server has it. Den chiefs come from their position
 * records and are left out of the youths and adults.
 */
export function buildSubUnitBaseline(input: {
  subUnitType: SubUnitType;
  subUnit: Pick<OrgSubUnit, 'subUnitId' | 'subUnitName' | 'denType'>;
  members: readonly SubUnitBaselineMember[];
  denChiefs?: DenChiefsByUserId;
}): SubUnitValues {
  const { subUnitType, subUnit, members, denChiefs = {} } = input;
  const isDen = isDenType(subUnitType);
  const inSubUnit = members.filter(
    member =>
      !!member.sbUserId &&
      !denChiefs[member.sbUserId] &&
      member.subUnits.some(
        ref => ref.type === subUnitType && ref.id === subUnit.subUnitId,
      ),
  );
  const idsOf = (kinds: MemberKind[]) =>
    uniq(
      inSubUnit
        .filter(member => kinds.includes(member.kind))
        .map(member => member.sbUserId),
    );

  return {
    name: subUnit.subUnitName,
    denType: isDen ? subUnit.denType ?? undefined : undefined,
    youthIds: idsOf([MemberKind.Youth, MemberKind.Participant]),
    adultIds: idsOf([MemberKind.Adult]),
    denChiefIds: isDen ? denChiefIdsInDen(denChiefs, subUnit.subUnitId) : [],
  };
}

/** The members the pickers mark `expired`, so payloads skip the same people */
export function expiredSbUserIds(
  members: readonly (Pick<RosterMemberView, 'sbUserId'> &
    SubUnitEligibilityMember)[],
): number[] {
  return uniq(
    members
      .filter(
        member => !!member.sbUserId && unavailableReason(member) === 'expired',
      )
      .map(member => member.sbUserId),
  );
}

// --- Mutation args -----------------------------------------------------------

interface SubUnitArgBase {
  orgGuid: string;
  subUnitType: SubUnitType;
  context: SubUnitPayloadContext;
}

const without = (ids: readonly number[], exclude: readonly number[]) => {
  const excluded = new Set(exclude);
  return uniq(ids).filter(id => !excluded.has(id));
};

function buildYouths(
  userIds: readonly number[],
  subUnitType: SubUnitType,
  denType: string | undefined,
  parents: readonly RosterParent[],
): AddYouthToSubUnitPayload[] {
  if (userIds.length === 0) return [];
  const positionId = youthPositionId(subUnitType, denType);
  return userIds.map(userId =>
    buildYouthPayload(
      userId,
      positionId,
      subUnitParentUserIds(parents, userId),
    ),
  );
}

function buildDenChiefAdds(
  userIds: readonly number[],
  context: SubUnitPayloadContext,
): PostAddDenChiefRequest[] {
  const { denChiefs = {}, packId } = context;
  return userIds
    .map(userId => denChiefs[userId])
    .filter((record): record is DenChiefRecord => !!record)
    .map(record => buildDenChiefAddPayload(record, packId));
}

function buildDenChiefRemoves(
  userIds: readonly number[],
  denChiefs: DenChiefsByUserId,
  subUnitId: number,
): PostRemoveDenChiefRequest[] {
  return userIds
    .map(userId => denChiefs[userId])
    .map(record => record && buildDenChiefRemovePayload(record, subUnitId))
    .filter((payload): payload is PostRemoveDenChiefRequest => !!payload);
}

function buildRenameBody(
  values: SubUnitValues,
  subUnitType: SubUnitType,
): PutSubUnitRequest {
  return {
    subUnitName: values.name.trim(),
    ...(isDenType(subUnitType) ? { denType: values.denType } : {}),
  };
}

/** Den chiefs without a record in `context.denChiefs` are skipped. */
export function buildCreateArg(
  input: SubUnitArgBase & { values: SubUnitValues },
): CreateSubUnitWithMembersArg {
  const { orgGuid, subUnitType, values, context } = input;
  const isDen = isDenType(subUnitType);
  const body: PostAddSubUnitRequest = {
    subUnitName: values.name.trim(),
    subUnitApproved: true,
    forumEnabled: false,
    ...(isDen ? { denType: values.denType, showDlEvents: true } : {}),
  };
  const denChiefIds = isDen ? uniq(values.denChiefIds) : [];
  const youthIds = without(values.youthIds, denChiefIds);
  const adultIds = without(values.adultIds, denChiefIds);

  return {
    orgGuid,
    subUnitType,
    body,
    youths: buildYouths(youthIds, subUnitType, values.denType, context.parents),
    adults: adultIds.map(userId => buildAdultPayload(userId, subUnitType)),
    denChiefs: buildDenChiefAdds(denChiefIds, context),
  };
}

interface LevelChangeInput {
  subUnitType: SubUnitType;
  values: Pick<SubUnitValues, 'denType'>;
  baseline: Pick<SubUnitValues, 'denType'>;
  /**
   * An earlier save changed the level but failed on the youths, who keep the
   * old position. The server data can't show it, so the caller tracks it.
   */
  pendingLevelChange?: boolean;
}

const denLevelChanged = (input: LevelChangeInput) =>
  isDenType(input.subUnitType) &&
  input.values.denType !== input.baseline.denType;

/** Re-post every youth so their position follows a changed or pending level */
export function repostsAllYouths(input: LevelChangeInput): boolean {
  return denLevelChanged(input) || !!input.pendingLevelChange;
}

/** Pending when a save that re-posts every youth fails on the youths */
export function nextPendingLevelChange(
  input: LevelChangeInput & { failed: readonly SubUnitSaveStep[] },
): boolean {
  return repostsAllYouths(input) && input.failed.includes('youths');
}

/**
 * Changes from the server `baseline`, so a retry sends only what's missing.
 * Den chiefs leave only through `removeDenChiefs`, never `DELETE …/users`.
 */
export function diffSubUnit(
  input: SubUnitArgBase &
    LevelChangeInput & {
      subUnitId: number;
      values: SubUnitValues;
      baseline: SubUnitValues;
    },
): UpdateSubUnitWithMembersArg {
  const { orgGuid, subUnitType, subUnitId, values, baseline, context } = input;
  const isDen = isDenType(subUnitType);
  const renamed =
    values.name.trim() !== baseline.name.trim() || denLevelChanged(input);

  const denChiefIds = isDen ? uniq(values.denChiefIds) : [];
  const baselineDenChiefIds = isDen ? uniq(baseline.denChiefIds) : [];
  const allDenChiefIds = [...denChiefIds, ...baselineDenChiefIds];

  const youthIds = without(values.youthIds, allDenChiefIds);
  const adultIds = without(values.adultIds, allDenChiefIds);
  const baselineYouthIds = without(baseline.youthIds, allDenChiefIds);
  const baselineAdultIds = without(baseline.adultIds, allDenChiefIds);

  // Expired youths are left as they are: posting them fails the whole batch
  const youthsToPost = without(
    youthIds,
    repostsAllYouths(input)
      ? context.expiredUserIds ?? []
      : [...baselineYouthIds, ...(context.expiredUserIds ?? [])],
  );

  return {
    orgGuid,
    subUnitType,
    subUnitId,
    ...(renamed ? { rename: buildRenameBody(values, subUnitType) } : {}),
    youths: buildYouths(
      youthsToPost,
      subUnitType,
      values.denType,
      context.parents,
    ),
    adults: without(adultIds, baselineAdultIds).map(userId =>
      buildAdultPayload(userId, subUnitType),
    ),
    addDenChiefs: buildDenChiefAdds(
      without(denChiefIds, baselineDenChiefIds),
      context,
    ),
    removeUserIds: uniq([
      ...without(baselineYouthIds, youthIds),
      ...without(baselineAdultIds, adultIds),
    ]),
    removeDenChiefs: buildDenChiefRemoves(
      without(baselineDenChiefIds, denChiefIds),
      context.denChiefs ?? {},
      subUnitId,
    ),
  };
}

/** Checks every request list, so a new kind of request can't be missed */
export function isNoopUpdate(arg: UpdateSubUnitWithMembersArg): boolean {
  const requests = omit(arg, ['orgGuid', 'subUnitType', 'subUnitId', 'rename']);
  return (
    !arg.rename && Object.values(requests).every(list => list.length === 0)
  );
}

/**
 * Unlike the baseline, includes people who left the roster: only an empty
 * sub-unit can be deleted.
 */
export function assignedSubUnitUserIds(
  records: ReadonlyArray<{
    sbUserId: number;
    subUnitInfo: ReadonlyArray<SubUnitAssignment>;
  }>,
  subUnitType: SubUnitType,
  subUnitId: number,
): number[] {
  const isAssigned = (info: SubUnitAssignment) => {
    const ref = assignmentRef(info);
    return ref?.type === subUnitType && ref.id === subUnitId;
  };
  return uniq(
    records
      .filter(record => record.subUnitInfo.some(isAssigned))
      .map(record => record.sbUserId),
  );
}

/** Empties the sub-unit, then deletes it. A retry rebuilds from the server. */
export function buildDeleteArg(input: {
  orgGuid: string;
  subUnitType: SubUnitType;
  subUnitId: number;
  baseline: Pick<SubUnitValues, 'youthIds' | 'adultIds' | 'denChiefIds'>;
  denChiefs?: DenChiefsByUserId;
  /** From `assignedSubUnitUserIds` */
  assignedUserIds?: readonly number[];
}): DeleteSubUnitWithMembersArg {
  const {
    orgGuid,
    subUnitType,
    subUnitId,
    baseline,
    denChiefs = {},
    assignedUserIds = [],
  } = input;
  const denChiefIds = isDenType(subUnitType) ? uniq(baseline.denChiefIds) : [];
  return {
    orgGuid,
    subUnitType,
    subUnitId,
    memberUserIds: without(
      uniq([...baseline.youthIds, ...baseline.adultIds, ...assignedUserIds]),
      denChiefIds,
    ),
    denChiefs: buildDenChiefRemoves(denChiefIds, denChiefs, subUnitId),
  };
}
